import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import FramedAvatar from '../components/FramedAvatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { renderWithMentions } from '../utils/mentionParser';
import ChatTextarea from '../components/chat/ChatTextarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  MessageSquare,
  Send,
  ArrowLeft,
  Search,
  User,
  Crown,
  Shield,
  Star
} from 'lucide-react';

const DirectMessagesPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const { recipientId } = useParams();
  
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [confirmRecipient, setConfirmRecipient] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unread, setUnread] = useState({});
  const [lastMessageAt, setLastMessageAt] = useState({});
  
  const messagesEndRef = useRef(null);
  // Ordre figé de la liste : le rafraîchissement des badges ne doit JAMAIS réordonner
  // les lignes sous le doigt (risque d'ouvrir — et d'écrire à — la mauvaise personne).
  const orderRef = useRef({ ready: false, unread: {}, lastMessageAt: {} });
  const recipientIdRef = useRef(null);
  const confirmedPeers = useRef(new Set());
  recipientIdRef.current = recipientId || null;

  // Le destinataire vient de l'URL (/dm/:recipientId) : une seule source de vérité
  const selectedMember = recipientId ? members.find((m) => m.id === recipientId) || null : null;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    fetchMembers();
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnread = async () => {
    try {
      const { data } = await api.get('/dm/unread-counts');
      setUnread(data.counts || {});
      setLastMessageAt(data.last_message_at || {});
      // L'ordre n'est recalculé qu'au premier chargement ou pendant qu'une conversation
      // est ouverte : jamais pendant que l'utilisateur parcourt la liste.
      if (!orderRef.current.ready || recipientIdRef.current) {
        orderRef.current = {
          ready: true,
          unread: data.counts || {},
          lastMessageAt: data.last_message_at || {},
        };
      }
    } catch (error) {
      // silencieux : le badge n'est pas critique
    }
  };

  useEffect(() => {
    if (recipientId) {
      fetchMessages(recipientId);
      fetchUnread();
    } else {
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMembers = async () => {
    try {
      const response = await api.get('/members');
      // Exclude current user
      setMembers(response.data.filter(m => m.id !== user.id));
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Erreur lors du chargement des membres');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (peerId) => {
    if (!peerId) return;
    try {
      const response = await api.get(`/dm/${peerId}/messages`);
      setMessages(response.data);
      setUnread((prev) => {
        const next = { ...prev };
        delete next[peerId];
        return next;
      });
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    const content = newMessage.trim();
    if (!content || !selectedMember) return;

    setSendingMessage(true);
    try {
      const response = await api.post('/messages', {
        content,
        recipient_id: selectedMember.id,
        // Contrôle serveur : le nom affiché doit correspondre au destinataire réel
        recipient_name: `${selectedMember.first_name} ${selectedMember.last_name || ''}`.trim()
      });
      setMessages([...messages, response.data]);
      setNewMessage('');
      confirmedPeers.current.add(selectedMember.id);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault?.();
    if (!newMessage.trim() || !selectedMember) return;

    // Premier message d'une conversation vide : on fait confirmer le destinataire
    // (évite d'écrire à la mauvaise personne après une erreur de ligne dans la liste)
    if (messages.length === 0 && !confirmedPeers.current.has(selectedMember.id)) {
      setConfirmRecipient(true);
      return;
    }
    await sendMessage();
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="w-3 h-3" />;
      case 'major': return <Crown className="w-3 h-3" />;
      case 'ambassadeur': return <Star className="w-3 h-3" />;
      default: return <User className="w-3 h-3" />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'major': return 'Major';
      case 'ambassadeur': return 'Ambassadeur';
      case 'odys_mentoring': return 'Mentoring';
      case 'odys_start': return 'Start';
      default: return role;
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'major': return 'role-major';
      case 'ambassadeur': return 'role-ambassadeur';
      case 'odys_mentoring': return 'role-odys_mentoring';
      case 'odys_start': return 'role-odys_start';
      default: return '';
    }
  };

  const filteredMembers = members
    .filter(m => `${m.first_name} ${m.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const snap = orderRef.current;
      const ua = snap.unread[a.id] || 0;
      const ub = snap.unread[b.id] || 0;
      if (ua !== ub) return ub - ua;
      const la = snap.lastMessageAt[a.id] || '';
      const lb = snap.lastMessageAt[b.id] || '';
      if (la !== lb) return lb.localeCompare(la);
      return `${a.first_name}`.localeCompare(`${b.first_name}`);
    });

  const totalUnread = Object.values(unread).reduce((sum, n) => sum + n, 0);

  // Homonymes : on affiche l'email pour les distinguer (évite d'écrire au mauvais compte)
  const nameCounts = members.reduce((acc, m) => {
    const key = `${m.first_name} ${m.last_name}`.toLowerCase().trim();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const isHomonym = (m) => nameCounts[`${m.first_name} ${m.last_name}`.toLowerCase().trim()] > 1;

  return (
    <div className="mobile-full-height md:h-[calc(100vh-3.5rem)] flex bg-background" data-testid="dm-page">
      {/* Noise Texture */}
      <div className="noise-overlay" />

      {/* Members Sidebar — plein écran sur mobile tant qu'aucune conversation n'est ouverte */}
      <aside
        className={`w-full md:w-80 border-r border-border bg-card/50 backdrop-blur-xl flex-col ${
          selectedMember ? 'hidden md:flex' : 'flex'
        }`}
        data-testid="dm-members-sidebar"
      >
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              <h1 className="font-heading font-bold text-lg">Messages privés</h1>
              {totalUnread > 0 && (
                <span
                  className="ml-1 min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center"
                  data-testid="dm-total-unread"
                >
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un membre..."
              className="pl-9 bg-secondary/50 border-border"
              data-testid="search-members-input"
            />
          </div>
        </div>

        {/* Members List */}
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {filteredMembers.map((member) => (
              <div
                key={member.id}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedMember?.id === member.id
                    ? 'bg-secondary/70 border-l-2 border-primary'
                    : 'hover:bg-secondary/30'
                }`}
                onClick={() => navigate(`/dm/${member.id}`)}
                data-testid={`dm-member-${member.id}`}
              >
                <FramedAvatar
                  src={member.avatar_url}
                  fallback={getInitials(member.first_name, member.last_name)}
                  frame={member.profile_frame}
                  role={member.role}
                  seniority={member.seniority}
                  size="lg"
                />
                <div className="flex-1 min-w-0">
                  <p className={`truncate ${unread[member.id] ? 'font-bold' : 'font-medium'}`}>
                    {member.first_name} {member.last_name}
                  </p>
                  <Badge className={`text-xs px-1.5 py-0 ${getRoleBadgeClass(member.role)}`}>
                    {getRoleIcon(member.role)}
                    <span className="ml-1">{getRoleLabel(member.role)}</span>
                  </Badge>
                  {isHomonym(member) && (
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5" data-testid={`dm-member-email-${member.id}`}>
                      {member.email}
                    </p>
                  )}
                </div>
                {unread[member.id] > 0 && (
                  <span
                    className="min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                    data-testid={`dm-unread-${member.id}`}
                  >
                    {unread[member.id] > 9 ? '9+' : unread[member.id]}
                  </span>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      </aside>

      {/* Chat Area */}
      <main className={`flex-1 flex-col min-w-0 ${selectedMember ? 'flex' : 'hidden md:flex'}`}>
        {selectedMember ? (
          <>
            {/* Chat Header */}
            <header className="h-16 border-b border-border flex items-center px-3 md:px-6 bg-card/30 backdrop-blur-sm">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden mr-1 shrink-0"
                onClick={() => navigate('/dm')}
                data-testid="dm-back-to-list"
                aria-label="Retour aux conversations"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="mr-3 shrink-0">
                <FramedAvatar
                  src={selectedMember.avatar_url}
                  fallback={getInitials(selectedMember.first_name, selectedMember.last_name)}
                  frame={selectedMember.profile_frame}
                  role={selectedMember.role}
                  seniority={selectedMember.seniority}
                  size="lg"
                />
              </div>
              <div className="min-w-0">
                <h2 className="font-heading font-semibold truncate">
                  {selectedMember.first_name} {selectedMember.last_name}
                </h2>
                <Badge className={`text-xs ${getRoleBadgeClass(selectedMember.role)}`}>
                  {getRoleLabel(selectedMember.role)}
                </Badge>
              </div>
            </header>

            {/* Messages */}
            <ScrollArea className="flex-1 p-3 md:p-6">
              <div className="space-y-4 max-w-3xl mx-auto">
                {messages.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Démarrez une conversation avec {selectedMember.first_name}</p>
                  </div>
                ) : (
                  messages.map((message, index) => (
                    <div
                      key={message.id}
                      className={`flex gap-3 animate-fade-in ${
                        message.author_id === user.id ? 'flex-row-reverse' : ''
                      }`}
                      style={{ animationDelay: `${index * 0.02}s` }}
                      data-testid={`dm-message-${message.id}`}
                    >
                      <FramedAvatar
                        src={message.author_avatar}
                        fallback={message.author_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                        frame={message.author_frame}
                        role={message.author_role}
                        size="chat"
                        className="flex-shrink-0"
                      />
                      <div className={`flex-1 max-w-[70%] ${message.author_id === user.id ? 'text-right' : ''}`}>
                        <div className={`flex items-center gap-2 mb-1 ${message.author_id === user.id ? 'justify-end' : ''}`}>
                          <span className="text-xs text-muted-foreground">
                            {new Date(message.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className={`${message.author_id === user.id ? 'message-bubble-own' : 'message-bubble'} inline-block`}>
                          <p className="text-sm break-words whitespace-pre-wrap text-left">{renderWithMentions(message.content, user?.first_name ? `${user.first_name}_${user.last_name || ''}`.toLowerCase() : null)}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-3 md:p-4 border-t border-border bg-card/80 backdrop-blur-sm">
              <div className="max-w-3xl mx-auto min-w-0">
                <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5" data-testid="dm-recipient-banner">
                  À&nbsp;:
                  <span className="font-semibold text-primary">
                    {selectedMember.first_name} {selectedMember.last_name}
                  </span>
                </p>
                <div className="flex gap-2 items-end min-w-0">
                <div className="flex-1 min-w-0">
                  <ChatTextarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onSubmit={handleSendMessage}
                    placeholder={`Message ${selectedMember.first_name}...`}
                    disabled={sendingMessage}
                    testId="dm-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={sendingMessage || !newMessage.trim()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 gold-glow shrink-0 h-11 w-12"
                  data-testid="dm-send-button"
                >
                  <Send className="w-4 h-4" />
                </Button>
                </div>
              </div>
            </form>

            <AlertDialog open={confirmRecipient} onOpenChange={setConfirmRecipient}>
              <AlertDialogContent data-testid="dm-confirm-recipient-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Envoyer à {selectedMember.first_name} {selectedMember.last_name} ?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    C'est ton premier message dans cette conversation. Vérifie le destinataire avant l'envoi —
                    ce message lui sera envoyé en privé, et il recevra une notification par email.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="flex items-center gap-3 rounded-lg border border-border bg-secondary/40 p-3">
                  <FramedAvatar
                    src={selectedMember.avatar_url}
                    fallback={getInitials(selectedMember.first_name, selectedMember.last_name)}
                    frame={selectedMember.profile_frame}
                    role={selectedMember.role}
                    seniority={selectedMember.seniority}
                    size="lg"
                  />
                  <div className="min-w-0">
                    <p className="font-semibold truncate">
                      {selectedMember.first_name} {selectedMember.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground">{getRoleLabel(selectedMember.role)}</p>
                  </div>
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="dm-confirm-recipient-cancel">
                    Ce n'est pas la bonne personne
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => { e.preventDefault(); setConfirmRecipient(false); sendMessage(); }}
                    data-testid="dm-confirm-recipient-confirm"
                  >
                    Oui, envoyer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Sélectionnez un membre pour démarrer une conversation</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DirectMessagesPage;
